import { useMemo } from 'react';

import { UseQueryResult } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import chunk from 'lodash/chunk';

import { Services } from '@/api/Services';
import type {
  ActiveOrdersRequest,
  ActiveOrdersResponse,
  DashboardWeatherOrder,
  DeploymentDashboardResponse,
  NotificationResponse,
} from '@/api/data-contracts';
import { useNotificationsService } from '@/features/notifications';
import { type TaskWeatherData, useWeatherService } from '@/features/service-tasks';
import { queryKeys } from '@/react-query/constants';
import { createFeatureApi, optionalApiMethod } from '@/shared/api';
import useUploadProgressService from '@/shared/api/uploadProgress.service';
import { type ErrorKind, getErrorKind } from '@/shared/lib/errors';

import {
  ALERT_EVENT_TYPES,
  DashboardAlertKind,
  latestPerOrderAndType,
  orderRefOf,
  sortAlerts,
  withoutInactiveOrders,
} from '../model/alerts';
import { OrderForecast, buildOrderForecasts, weatherTaskIds } from '../model/orderForecasts';
import { Shipment } from '../model/shipments';

const { keys, useFeatureQuery } = createFeatureApi(queryKeys.servicesDashboard);

const WEATHER_BATCH_SIZE = 200;
const ACTIVE_ORDERS_BATCH_SIZE = 100;
const ALERTS_PAGE_SIZE = 100;
const SERVICES_MODULE = 'SERVICES';

const EMPTY_WEATHER_ORDERS: DashboardWeatherOrder[] = [];
const EMPTY_WEATHER = new Map<string, TaskWeatherData>();
const MAX_RETRIES = 1;

type RequestOptions = { signal?: AbortSignal };

type GetDeploymentDashboard = (
  query: { from: string; to: string },
  params?: RequestOptions,
) => Promise<{ data: DeploymentDashboardResponse }>;

type GetActiveOrders = (
  request: ActiveOrdersRequest,
  params?: RequestOptions,
) => Promise<{ data: ActiveOrdersResponse }>;

const toApiDate = (day: Dayjs): string => day.format('YYYY-MM-DD');

const RETRYABLE_ERRORS: ErrorKind[] = ['network', 'server'];

const retryTransientErrors = (failureCount: number, error: unknown): boolean =>
  RETRYABLE_ERRORS.includes(getErrorKind(error)) && failureCount < MAX_RETRIES;

export const useDeploymentDashboardQuery = (
  from: Dayjs,
  to: Dayjs,
): UseQueryResult<DeploymentDashboardResponse> & { isSupported: boolean } => {
  const servicesApi = useUploadProgressService(Services);
  const getDashboard = optionalApiMethod<GetDeploymentDashboard>(
    servicesApi,
    'getDeploymentDashboard',
  );
  const period = { from: toApiDate(from), to: toApiDate(to) };
  const isSupported = !!getDashboard;

  const query = useFeatureQuery<DeploymentDashboardResponse>({
    queryKey: keys.sub('deployment', period),
    queryFn: async (signal) => {
      if (!getDashboard) throw new Error('getDeploymentDashboard is not available');
      const { data } = await getDashboard(period, { signal });
      return data;
    },
    enabled: isSupported,
    staleTime: 0,
    retry: retryTransientErrors,
  });

  return { ...query, isSupported };
};

const useDashboardWeatherQuery = (orders: DashboardWeatherOrder[]) => {
  const { getTasksWeather } = useWeatherService();
  const taskIds = weatherTaskIds(orders);

  return useFeatureQuery<Map<string, TaskWeatherData>>({
    queryKey: keys.sub('weather', taskIds),
    queryFn: async (signal) => {
      const batches: string[][] = [];
      for (let i = 0; i < taskIds.length; i += WEATHER_BATCH_SIZE) {
        batches.push(taskIds.slice(i, i + WEATHER_BATCH_SIZE));
      }
      const results = await Promise.all(batches.map((batch) => getTasksWeather(batch, signal)));
      return new Map(results.flatMap((result) => [...result.entries()]));
    },
    enabled: taskIds.length > 0,
  });
};

export const useDashboardForecasts = (
  orders: DashboardWeatherOrder[] | undefined,
): { forecasts: OrderForecast[]; isLoading: boolean } => {
  const weatherOrders = orders ?? EMPTY_WEATHER_ORDERS;
  const weather = useDashboardWeatherQuery(weatherOrders);
  const forecasts = useMemo(
    () => buildOrderForecasts(weatherOrders, weather.data ?? EMPTY_WEATHER),
    [weatherOrders, weather.data],
  );
  return { forecasts, isLoading: weather.isLoading && weather.fetchStatus !== 'idle' };
};

const NO_SHIPMENTS: Shipment[] = [];

export const useDashboardShipments = (): Shipment[] => NO_SHIPMENTS;

const useActiveOrderFilter = () => {
  const servicesApi = useUploadProgressService(Services);
  const getActiveOrders = optionalApiMethod<GetActiveOrders>(
    servicesApi,
    'getDeploymentDashboardActiveOrders',
  );

  return async (
    alerts: NotificationResponse[],
    signal: AbortSignal | undefined,
  ): Promise<NotificationResponse[]> => {
    const orderRefs = [...new Set(alerts.map(orderRefOf).filter((ref): ref is string => !!ref))];
    if (!getActiveOrders || !orderRefs.length) return alerts;
    try {
      const answers = await Promise.all(
        chunk(orderRefs, ACTIVE_ORDERS_BATCH_SIZE).map((batch) =>
          getActiveOrders({ orderRefs: batch }, { signal }),
        ),
      );
      return withoutInactiveOrders(
        alerts,
        new Set(answers.flatMap(({ data }) => data.activeOrderRefs)),
      );
    } catch {
      return alerts;
    }
  };
};

export const useDashboardAlertsQuery = (
  kind: DashboardAlertKind,
  from: Dayjs,
  to: Dayjs,
): UseQueryResult<NotificationResponse[]> => {
  const { getNotifications } = useNotificationsService();
  const keepActiveOrders = useActiveOrderFilter();
  const start = from.startOf('day').toISOString();
  const end = to.endOf('day').toISOString();

  return useFeatureQuery<NotificationResponse[]>({
    queryKey: keys.sub('alerts', kind, { start, end }),
    queryFn: async (signal) => {
      const alerts = await getNotifications(
        {
          pageNumber: 0,
          numberOfItems: ALERTS_PAGE_SIZE,
          start,
          end,
          eventTypes: ALERT_EVENT_TYPES[kind],
          targetModule: SERVICES_MODULE,
        },
        signal,
      );
      return sortAlerts(await keepActiveOrders(latestPerOrderAndType(alerts), signal));
    },
    staleTime: 0,
  });
};
